import Map, {Marker, ViewStateChangeEvent, NavigationControl, AttributionControl} from "react-map-gl/maplibre";
import maplibregl, {LngLatBoundsLike} from "maplibre-gl";
import {Protocol} from "pmtiles";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {MapStateStorage} from "./MapStateStorage";
import {Station} from "../../utils/model/Station";
import GMapCluster from "./GMapCluster";
import GMapMarker from "./GMapMarker";
import {useParams} from "react-router-dom";
import {normalizeFuelType} from "../../utils/model/FuelType";
import GoogleMapReact from "google-map-react";
import "maplibre-gl/dist/maplibre-gl.css";
import {useRouter} from "../../context/RouterContext";
import {Location} from "../../utils/model/Location";
import Puller from "../station/components/Puller";
import {StationComponent} from "../station/StationComponent";
import {CircularProgress, SwipeableDrawer, SwipeableDrawerProps} from "@mui/material";
import GeolocationEnabledDialog from "../station/components/GeolocationEnabledDialog";
import {IsWebviewStorage} from "./IsWebviewStorage";
import CenterOnMe from "./components/CenterOnMe";
import GMapUserMarker from "./GMapUserMarker";
import {layers, namedFlavor} from "@protomaps/basemaps";
import {useTranslation} from "react-i18next";
import {Bbox, getStationById, getStationsByBbox} from "../../utils/api/stationApi";
import {BboxCluster, BboxResult, BboxStation} from "../../utils/model/BboxResult";

export interface MapViewport {
    zoom: number;
    bounds: any;
    center: GoogleMapReact.Coords;
    viewportDate: Date;
}

const SWISS_ZOOM = 8;
const SWISS_CLUSTER_BOUNDS = [3.497052742187492, 44.392533837958695, 12.956281257812492, 49];
const SWISS_CENTER: GoogleMapReact.Coords = {lat: 46.801111, lng: 8.226667}
const DEFAULT_MAP_VIEWPORT: MapViewport = {
    zoom: SWISS_ZOOM,
    bounds: SWISS_CLUSTER_BOUNDS,
    center: SWISS_CENTER,
    viewportDate: new Date()
}
const DEFAULT_PANBY_Y_MARGIN = 120;
const MARKER_HEIGHT = 55;

const SWISS_BOUNDS: LngLatBoundsLike = [[2, 44], [15, 49]];
const ONCENTER_MIN_ZOOMLEVEL = 15;

const BBOX_FETCH_DEBOUNCE_MS = 300;

const GEOLOCATION_OPTIONS = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 15000
};

const config = {
    host: "https://tcsmaps.ch",
    map: "planet",
};

export default function MMap() {
    const {i18n} = useTranslation();

    const {fuelId, stationId} = useParams();
    const fuelType = normalizeFuelType(fuelId);

    // If URL has a non-canonical fuelId (wrong case, unknown fuel),
    // rewrite the address bar to the canonical form without adding a
    // history entry.
    useEffect(() => {
        if (fuelId && fuelId !== fuelType && window.location.pathname.endsWith(`/${fuelId}`)) {
            const newPath = window.location.pathname.slice(0, -fuelId.length) + fuelType;
            window.history.replaceState(null, "", newPath + window.location.search);
        }
    }, [fuelId, fuelType]);

    const {navigate} = useRouter();
    const mapRef = useRef<any>();

    const isWebview = IsWebviewStorage.getIsWebview();

    const [bboxResults, setBboxResults] = useState<BboxResult[]>([]);
    const [selectedStation, setSelectedStation] = useState<Station | null>(null);
    const [selectedStationLoading, setSelectedStationLoading] = useState(false);

    const [currentMapViewport, setCurrentMapViewport] = useState<MapViewport>(() => {
        const latestViewport = MapStateStorage.getLatestMapViewport();

        // If station is selected, keep latest zoom but let the detail fetch set the center
        if (stationId) {
            return {
                zoom: latestViewport?.zoom ?? ONCENTER_MIN_ZOOMLEVEL,
                center: latestViewport?.center ?? SWISS_CENTER,
                bounds: latestViewport?.bounds ?? SWISS_CLUSTER_BOUNDS,
                viewportDate: new Date()
            };
        }

        if (latestViewport && MapStateStorage.isViewPortRecent(latestViewport)) {
            return latestViewport;
        }

        const latestKnownGeolocalisedViewport = MapStateStorage.getLatestKnownPosition();
        const isError = MapStateStorage.isLatestGeolocationRequestAnError();
        if (latestKnownGeolocalisedViewport && isError === false) {
            return latestKnownGeolocalisedViewport;
        }

        return latestViewport ?? DEFAULT_MAP_VIEWPORT;
    });

    const [initialMapViewport] = useState<MapViewport>(currentMapViewport);

    const [userCenter, setUserCenter] = useState<GoogleMapReact.Coords | undefined>(() => {
        const isError = MapStateStorage.isLatestGeolocationRequestAnError()
        if (isError === true) { return undefined }
        return MapStateStorage.getLatestKnownPosition()?.center ?? undefined
    });
    const [isDetectingLocation, setIsDetectingLocation] = useState(false);
    const [geolocationError, setGeolocationError] = useState<GeolocationPositionError | null>(null);
    const [geolocationDialog, setGeolocationDialog] = useState<boolean>(true);

    const [canCenterToUserOnInitialLoadValue] = useState<boolean>(() => {
        if (stationId) { return false; }
        const latestViewport = MapStateStorage.getLatestMapViewport()
        if (latestViewport && MapStateStorage.isViewPortRecent(latestViewport)) { return false; }
        const isError = MapStateStorage.isLatestGeolocationRequestAnError()
        return isError === false;
    })
    const canCenterToUserOnInitialLoadRef = useRef<boolean>(canCenterToUserOnInitialLoadValue);

    // Fetch bbox results on viewport or fuel change (debounced)
    useEffect(() => {
        const bounds = currentMapViewport.bounds;
        if (!Array.isArray(bounds) || bounds.length !== 4) { return; }
        if (!fuelType) { return; }

        const timer = setTimeout(async () => {
            try {
                const results = await getStationsByBbox({
                    zoom: Math.round(currentMapViewport.zoom),
                    pixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : 1,
                    bbox: bounds as Bbox,
                    filters: {fuel: fuelType, brands: []},
                });
                // Defensive: drop entries without a usable price so we never render NaN
                const sanitized = results.filter(item => typeof item.price === "number" && !Number.isNaN(item.price));
                setBboxResults(sanitized);
            } catch (err) {
                console.error("Failed to fetch stations by bbox", err);
            }
        }, BBOX_FETCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [currentMapViewport.bounds, currentMapViewport.zoom, fuelType]);

    // Returns the fetched station so callers can tell whether the backend data
    // actually changed (the price recompute after a submission is asynchronous).
    const refreshSelectedStation = useCallback(async (): Promise<Station | void> => {
        if (!stationId) { return; }
        try {
            const station = await getStationById(stationId);
            setSelectedStation(station);
            return station;
        } catch (err) {
            console.error("Failed to refresh station detail", err);
        }
    }, [stationId]);

    // Fetch selected station detail when stationId is in URL
    useEffect(() => {
        if (!stationId) {
            setSelectedStation(null);
            return;
        }
        let cancelled = false;
        setSelectedStationLoading(true);
        getStationById(stationId)
            .then(station => { if (!cancelled) { setSelectedStation(station); } })
            .catch(err => { console.error("Failed to fetch station detail", err); })
            .finally(() => { if (!cancelled) { setSelectedStationLoading(false); } });
        return () => { cancelled = true; };
    }, [stationId]);

    const centerOnMe = useCallback(async (initialLoad: Boolean) => {
        if (!isWebview) {
            if (!navigator.geolocation) { return; }
            if (initialLoad && navigator.permissions) {
                const geolocationPermissions = await navigator.permissions.query({name: 'geolocation'})
                if (geolocationPermissions.state !== 'granted') { return; }
            }
        }

        setIsDetectingLocation(true);
        navigator.geolocation.getCurrentPosition((position) => {
            const coords = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
            }
            const zoom = Math.max(currentMapViewport.zoom, ONCENTER_MIN_ZOOMLEVEL);

            if (mapRef.current && (!initialLoad || canCenterToUserOnInitialLoadRef.current)) {
                setTimeout(function () {
                    mapRef?.current?.setZoom(zoom);
                    mapRef?.current?.panTo(coords);
                }, 100);
            }
            setIsDetectingLocation(false);
            setGeolocationError(null);
            setUserCenter(coords);

            const viewport: MapViewport = {zoom: zoom, bounds: currentMapViewport.bounds, center: coords, viewportDate: new Date()};
            MapStateStorage.saveLatestKnownPosition(viewport);
            MapStateStorage.saveIsLatestGeolocationRequestAnError(false);
        }, (error) => {
            setIsDetectingLocation(false);
            setGeolocationError(error);
            setGeolocationDialog(true);
            MapStateStorage.saveIsLatestGeolocationRequestAnError(true);
        }, GEOLOCATION_OPTIONS);
    }, [currentMapViewport, isWebview])

    const zoomToSelectedStation = useCallback(() => {
        if (!mapRef.current) { return }
        if (!selectedStation) { return }

        let YMarginForPanBy = DEFAULT_PANBY_Y_MARGIN;
        const header = document.querySelector('.topBar') as HTMLElement | null;
        const bottomDrawer = document.querySelector('#drawerBlock') as HTMLElement | null;
        if (header != null && bottomDrawer != null) {
            const mapSizeViewport = window.innerHeight - header.offsetHeight;
            const mapCenter = mapSizeViewport / 2
            const drawerHeight = bottomDrawer.offsetHeight;
            const remainingHeight = mapSizeViewport - drawerHeight;
            const middleOfRemainingHeightFromDrawerTop = remainingHeight / 2;
            YMarginForPanBy = mapCenter - middleOfRemainingHeightFromDrawerTop - MARKER_HEIGHT - MARKER_HEIGHT / 2;
        }
        mapRef.current.flyTo({
            center: [selectedStation.location.lng, selectedStation.location.lat],
            duration: 500,
            offset: [0, -YMarginForPanBy]
        });

        canCenterToUserOnInitialLoadRef.current = false
    }, [mapRef, selectedStation])

    useEffect(() => {
        if (selectedStation) {
            zoomToSelectedStation()
        }
    }, [selectedStation, zoomToSelectedStation]);

    const onClusterClick = (cluster: BboxCluster) => {
        if (selectedStation) {
            navigate(`/map/${fuelId}`);
        }
        if (mapRef.current) {
            mapRef.current.easeTo({
                zoom: Math.min(cluster.zoom + 2, 20),
                center: [cluster.longitude, cluster.latitude],
                duration: 500
            });
        }
    }

    const onStationClick = (station: BboxStation) => {
        navigate(`/station/${station.id}/${fuelId}`);
    }

    const clickOutsideDrawer = () => {
        if (selectedStation) {
            navigate(`/map/${fuelId}`);
        }
    }

    const toggleDrawer = (open: boolean) => () => {
        if (!open) {
            navigate(`/map/${fuelId}`)
        }
    };

    useEffect(() => {
        let protocol = new Protocol();
        maplibregl.addProtocol("pmtiles", protocol.tile);
        return () => {
            maplibregl.removeProtocol("pmtiles");
        };
    }, []);

    const onMoveEnd = (event: ViewStateChangeEvent) => {
        const bounds = event.target.getBounds();
        const newBounds = [
            bounds.getSouthWest().lng,
            bounds.getSouthWest().lat,
            bounds.getNorthEast().lng,
            bounds.getNorthEast().lat
        ];

        const mMapCenter = event.target.getBounds().getCenter();
        let center: Location;
        if (mMapCenter) {
            center = {lat: mMapCenter.lat, lng: mMapCenter.lng};
        } else {
            const localZoomState = MapStateStorage.getLatestMapViewport();
            if (!localZoomState) {
                center = SWISS_CENTER;
            } else {
                center = localZoomState.center ?? SWISS_CENTER;
            }
        }

        const viewport: MapViewport = {
            zoom: Math.round(event.target.getZoom()),
            bounds: newBounds,
            center: center,
            viewportDate: new Date()
        };
        MapStateStorage.saveMapViewport(viewport);
        setCurrentMapViewport(viewport);
    }

    const onDragEnd = (() => {
        canCenterToUserOnInitialLoadRef.current = false;
    })

    const markers = useMemo(() => bboxResults.map(item => {
            if (item.cluster) {
                return <Marker key={`cluster-${item.id}`} latitude={item.latitude} longitude={item.longitude}>
                    <GMapCluster cluster={item} onClusterClick={onClusterClick}/>
                </Marker>
            }
            const active = item.id === selectedStation?.id;
            return <Marker style={{zIndex: active ? 1 : "unset"}} key={`station-${item.id}`} latitude={item.latitude} longitude={item.longitude}>
                <GMapMarker
                    station={item}
                    isCheapest={item.isCheapest}
                    onStationClick={onStationClick}
                    active={active}/>
            </Marker>
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [bboxResults, selectedStation?.id]);

    const drawerBleeding = 56;
    const swipeableDrawerProps: SwipeableDrawerProps = {
        open: Boolean(stationId),
        onClose: toggleDrawer(false),
        onOpen: toggleDrawer(true),
        anchor: "bottom",
        swipeAreaWidth: drawerBleeding,
        disableSwipeToOpen: true,
        variant: "temporary",
        PaperProps: {
            sx: {display: 'flex'},
            id: "drawerBlock",
        },
        ModalProps: {
            keepMounted: true,
            sx: {top: window.innerHeight},
            BackdropProps: {
                invisible: true,
                onMouseDown: toggleDrawer(false),
                sx: {top: window.innerHeight},
            }
        }
    }

    const onMapLoad = useCallback(() => {
        const map = mapRef.current.getMap();
        map.keyboard.disableRotation();
        map.touchZoomRotate.disableRotation();
    }, []);

    return (
        <>
            {geolocationDialog && geolocationError &&
                <GeolocationEnabledDialog isGeolocationEnabled={false} onClose={() => setGeolocationDialog(false)}/>
            }
            <div className='google-map-inner'>
                <Map
                    ref={mapRef}
                    onDragEnd={onDragEnd}
                    onClick={clickOutsideDrawer}
                    onMoveEnd={onMoveEnd}
                    onLoad={onMapLoad}
                    touchPitch={false}
                    dragRotate={false}
                    touchZoomRotate={true}
                    minZoom={6}
                    maxBounds={SWISS_BOUNDS}
                    initialViewState={{
                        latitude: initialMapViewport.center.lat,
                        longitude: initialMapViewport.center.lng,
                        zoom: initialMapViewport.zoom,
                    }}
                    mapStyle={{
                        version: 8,
                        sources: {
                            protomaps: {
                                maxzoom: 15,
                                type: "vector",
                                tiles: [`${config.host}/${config.map}/{z}/{x}/{y}.mvt`],
                            },
                        },
                        glyphs: `${config.host}/fonts/{fontstack}/{range}.pbf`,
                        layers: layers("protomaps", namedFlavor("light"), {
                            lang: i18n.language || "en"
                        }).filter((layer) => layer.id !== "pois"),
                    }}
                    mapLib={maplibregl as any}
                >
                    {userCenter &&
                        <Marker latitude={userCenter.lat} longitude={userCenter.lng}>
                            <GMapUserMarker key='user-point' {...userCenter}/>
                        </Marker>
                    }
                    {markers}

                    <AttributionControl customAttribution="OpenStreetMap"/>

                    <NavigationControl showCompass={false} position={'bottom-right'}/>
                </Map>
                {navigator.geolocation && (
                    <div className='center-on-me'>
                        <CenterOnMe onClick={() => {centerOnMe(false)}} loading={isDetectingLocation}/>
                    </div>
                )}
            </div>
            <SwipeableDrawer className="drawer" {...swipeableDrawerProps}>
                <Puller/>
                {selectedStation
                    ? <StationComponent station={selectedStation} drawerBleeding={drawerBleeding} onRefresh={refreshSelectedStation}/>
                    : selectedStationLoading
                        ? <CircularProgress color="secondary" sx={{margin: "auto", my: 4}}/>
                        : null}
            </SwipeableDrawer>
        </>
    );
}
